import { useEffect, useCallback, useState } from 'react';
import { webSocketService } from '../services/websocketService';
import { Event } from '../types/event';

interface UseRealTimeEventsOptions {
  eventId?: string;
  autoConnect?: boolean;
  onEventUpdate?: (event: Event) => void;
  onEventCreated?: (event: Event) => void;
  onEventDeleted?: (eventId: string) => void;
  onGuestResponse?: (data: any) => void;
  onConnectionChange?: (connected: boolean) => void;
}

interface UseRealTimeEventsReturn {
  isConnected: boolean;
  activePollingCount: number;
  startPolling: () => string;
  stopPolling: (pollId?: string) => void;
  refreshData: (dataType?: 'events' | 'categories' | 'series') => Promise<void>;
}

export const useRealTimeEvents = (options: UseRealTimeEventsOptions = {}): UseRealTimeEventsReturn => {
  const {
    eventId,
    autoConnect = true,
    onEventUpdate,
    onEventCreated,
    onEventDeleted,
    onGuestResponse,
    onConnectionChange,
  } = options;

  const [isConnected, setIsConnected] = useState(webSocketService.connectionStatus);
  const [activePollingCount, setActivePollingCount] = useState(webSocketService.activePollingCount);
  const [currentPollId, setCurrentPollId] = useState<string | null>(null);

  // Event handlers
  const handleEventUpdate = useCallback((event: Event) => {
    onEventUpdate?.(event);
  }, [onEventUpdate]);

  const handleEventCreated = useCallback((event: Event) => {
    onEventCreated?.(event);
  }, [onEventCreated]);

  const handleEventDeleted = useCallback((data: { id: string }) => {
    onEventDeleted?.(data.id);
  }, [onEventDeleted]);

  const handleGuestResponse = useCallback((data: any) => {
    onGuestResponse?.(data);
  }, [onGuestResponse]);

  const handleConnectionChange = useCallback((data: { connected: boolean }) => {
    setIsConnected(data.connected);
    setActivePollingCount(webSocketService.activePollingCount);
    onConnectionChange?.(data.connected);
  }, [onConnectionChange]);

  // Setup event listeners
  useEffect(() => {
    const unsubscribeEventUpdate = webSocketService.on('event_updated', handleEventUpdate);
    const unsubscribeEventCreated = webSocketService.on('event_created', handleEventCreated);
    const unsubscribeEventDeleted = webSocketService.on('event_deleted', handleEventDeleted);
    const unsubscribeGuestResponse = webSocketService.on('guest_response', handleGuestResponse);
    const unsubscribeConnectionStatus = webSocketService.on('connection_status', handleConnectionChange);

    return () => {
      unsubscribeEventUpdate();
      unsubscribeEventCreated();
      unsubscribeEventDeleted();
      unsubscribeGuestResponse();
      unsubscribeConnectionStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove handler functions from dependencies to prevent re-subscription

  // Auto-connect WebSocket
  useEffect(() => {
    if (autoConnect && !currentPollId) {
      const pollId = webSocketService.startEventPolling(eventId);
      setCurrentPollId(pollId);
      setActivePollingCount(webSocketService.activePollingCount);
    }

    return () => {
      if (currentPollId) {
        webSocketService.stopPolling(currentPollId);
        setCurrentPollId(null);
        setActivePollingCount(webSocketService.activePollingCount);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, eventId]); // Don't include currentPollId to avoid loop

  // Manual WebSocket control
  const startPolling = useCallback(() => {
    if (currentPollId) {
      webSocketService.stopPolling(currentPollId);
    }
    
    const pollId = webSocketService.startEventPolling(eventId);
    setCurrentPollId(pollId);
    setActivePollingCount(webSocketService.activePollingCount);
    return pollId;
  }, [eventId, currentPollId]);

  const stopPolling = useCallback((pollId?: string) => {
    const targetPollId = pollId || currentPollId;
    if (targetPollId) {
      webSocketService.stopPolling(targetPollId);
      if (targetPollId === currentPollId) {
        setCurrentPollId(null);
      }
      setActivePollingCount(webSocketService.activePollingCount);
    }
  }, [currentPollId]);

  const refreshData = useCallback(async (dataType: 'events' | 'categories' | 'series' = 'events') => {
    await webSocketService.refreshData(dataType);
  }, []);

  return {
    isConnected,
    activePollingCount,
    startPolling,
    stopPolling,
    refreshData,
  };
};

export default useRealTimeEvents;
